// ============================================================
// ActionBox.tsx — THE note / callout used everywhere a coloured box tells the
// user something (settlement, statement, send windows, bills, credit notes,
// client details, settings).
//
// (Audit fix batch 2, 31 Oct — the new design, from design/action-box/
// options 1 + 2.) Option 2's shell for every tone: a calm white card with a
// coloured ACCENT STRIP and an ICON CHIP (amber `warn` = needs doing, red
// `danger` = a problem, green `info` / `done` = done or information), and a
// title that says the outcome — no more full tinted blocks.
//   • SINGLE notice (`message` + `actions`): the facts as short lines.
//     (D12, 2 Nov) ONE action (or one raw `footer` node, e.g. ErrorNote's
//     Dismiss / a Banner's Retry, or a lone <button> passed as children) sits
//     at the RIGHT of the title/text row inside the card, wrapping under the
//     text on a narrow screen, with NO footer strip. Several actions (or a
//     `form` the action belongs to) go in a bottom row under a plain divider:
//     quiet text links on the LEFT, the primary on the RIGHT. Never a tinted
//     band behind buttons or forms.
//   • CHECKLIST (`items`, several steps): Option 1's rows inside the shell —
//     a circle (green tick once `done`), the fact + an optional grey `hint`,
//     the row's action on the RIGHT of its own row (the main next step = a
//     small solid green button; a shortcut to a field = a quiet link with →),
//     and "1 of 3 done" in the header. Box-level `actions` go in the bottom
//     row (plain divider, no tint).
//   • SLIM (`variant="slim"`): pure information with no real step (Figures
//     locked; a consent being recorded beneath) — one line, a link on the right.
// A removal (`kind: 'danger'`) is a RED primary button (with the bin icon).
//
// Accessibility: a labelled group (`ariaLabel`, else the title) — a live
// region only with `live` (in answer to something the user just did), or
// `role="alert"` for a problem that must interrupt. A disabled button keeps
// its reason in visible text next to it, linked with aria-describedby. DOM
// order = visual order (links before the primary, a row's text before its
// action). Colours are casevo-* tokens only (light + dark).
// (Big-audit fix batch 1) An action can carry a `tip`: a hover / focus
// tooltip (RailTooltip) whose text is also the button's description
// (aria-describedby → a hidden copy, so it's read without hovering). A box
// can be named / described by elements inside it (`labelledBy` /
// `describedBy`, e.g. a heading that focus moves to).
// ============================================================
import React, { useId } from 'react'
import { BIcon, B_PRIMARY, NOTE, type BIconName } from '../billing/billingUi'
import RailTooltip from './RailTooltip'

export type ActionKind = 'primary' | 'link' | 'danger'
export interface ActionBoxAction {
  label: string
  onClick: () => void
  // The look: the main step (solid green), a quiet text link, a removal (solid red).
  kind?: ActionKind
  // (Older callers) `primary: true` = kind 'primary'; otherwise a link.
  primary?: boolean
  // A link that only takes you to a field / place shows "→".
  arrow?: boolean
  icon?: BIconName
  disabled?: boolean
  // Shown as visible text beside the button while it is disabled.
  disabledReason?: string
  busy?: boolean
  busyLabel?: string
  // A stable hook for tests / focus moves.
  id?: string
  // What the action gives you, in a sentence: a tooltip on hover / focus and
  // the button's accessible description.
  tip?: string
}
export interface ActionBoxItem {
  key: string
  // The fact (short), and an optional grey hint under it.
  message: React.ReactNode
  hint?: React.ReactNode
  // Done → a green tick (counts towards "1 of 3 done").
  done?: boolean
  actions?: ActionBoxAction[]
}
export type ActionBoxTone = 'warn' | 'danger' | 'info' | 'done'

const TONE: Record<ActionBoxTone, { strip: string; chip: string; icon: BIconName }> = {
  warn: { strip: 'bg-casevo-gold', chip: 'bg-casevo-warn-bg text-casevo-warn', icon: 'alert' },
  danger: { strip: 'bg-casevo-danger', chip: 'bg-casevo-danger-bg text-casevo-danger', icon: 'alert' },
  info: { strip: 'bg-casevo-light', chip: 'bg-casevo-mint text-casevo-brand', icon: 'info' },
  done: { strip: 'bg-casevo-light', chip: 'bg-casevo-mint text-casevo-brand', icon: 'check' },
}
// (D12) The box's own actions (inline or bottom row): a 44px touch target on
// a phone, the compact 30px look from `sm` up.
const TOUCH = 'min-h-[44px] sm:min-h-[30px]'
export const B_SM_PRIMARY = B_PRIMARY.replace('h-9', 'h-[30px]').replace('px-[14px]', 'px-[12px]').replace('text-[13.5px]', 'text-[12.5px]')
// A removal: solid red (white on red in light; dark text on the light red in dark).
export const B_SM_DANGER = 'inline-flex items-center justify-center gap-[6px] font-semibold h-[30px] px-[12px] rounded-[10px] text-[12.5px] border border-casevo-danger bg-casevo-danger text-casevo-surface hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none casevo-focus'
export const LINK = 'inline-flex items-center gap-[4px] text-[13px] font-medium text-casevo-link hover:underline underline-offset-2 disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed focus:outline-none casevo-focus rounded-[4px]'

const kindOf = (a: ActionBoxAction): ActionKind => a.kind ?? (a.primary ? 'primary' : 'link')

// One action (+ its visible "why" when disabled, linked to the button).
const Action: React.FC<{ a: ActionBoxAction; whyId: string; touch?: boolean }> = ({ a, whyId, touch }) => {
  const k = kindOf(a)
  // A link that wraps on a phone reads from the left, beside its icon (a <button> centres by default).
  const cls = `${k === 'primary' ? B_SM_PRIMARY : k === 'danger' ? B_SM_DANGER : `${LINK} text-left`}${touch ? ` ${TOUCH}` : ''}`
  const showWhy = !!a.disabled && !!a.disabledReason
  const tipId = `${whyId}-tip`
  const describedBy = [showWhy ? whyId : '', a.tip ? tipId : ''].filter(Boolean).join(' ') || undefined
  const button = (
    <button type="button" id={a.id} className={cls} disabled={a.disabled || a.busy} onClick={a.onClick}
      aria-describedby={describedBy} data-kind={k}>
      {a.icon && <BIcon name={a.icon} size={12} />}
      {a.busy ? (a.busyLabel ?? `${a.label}…`) : a.label}
      {k === 'link' && a.arrow && <span aria-hidden="true">→</span>}
    </button>
  )
  return (
    <span className="inline-flex items-center gap-[8px] flex-wrap justify-end">
      {showWhy && k !== 'link' && <span id={whyId} className={`${NOTE} text-right`}>{a.disabledReason}</span>}
      {a.tip
        ? <RailTooltip as="span" className="inline-flex" enabled placement="below" wrap label={a.tip}>{button}<span id={tipId} className="sr-only">{a.tip}</span></RailTooltip>
        : button}
      {showWhy && k === 'link' && <span id={whyId} className={NOTE}>{a.disabledReason}</span>}
    </span>
  )
}

// (Kept for older callers) A row of actions — links first, the primary last.
export const ActionButtons: React.FC<{ actions: ActionBoxAction[] }> = ({ actions }) => {
  const id = useId()
  return (
    <div className="flex items-center gap-[14px] flex-wrap">
      {[...actions].sort((x, y) => Number(kindOf(x) !== 'link') - Number(kindOf(y) !== 'link')).map((a, i) => <Action key={a.label} a={a} whyId={`${id}-why-${i}`} />)}
    </div>
  )
}

const ActionBox: React.FC<{
  title?: React.ReactNode
  tone?: ActionBoxTone
  variant?: 'card' | 'slim'
  icon?: BIconName
  // One notice: its facts …
  message?: React.ReactNode
  // … and its actions: ONE → at the right of the title/text row; several →
  // a bottom row under a plain divider (links left, the primary right).
  actions?: ActionBoxAction[]
  // Several steps: the checklist rows.
  items?: ActionBoxItem[]
  // Slim: a short status after the title ("· recording…").
  status?: React.ReactNode
  ariaLabel?: string
  // Named / described by elements inside it (instead of `ariaLabel`).
  labelledBy?: string
  describedBy?: string
  live?: boolean
  // A problem that must interrupt (a refused save, a failed load).
  alert?: boolean
  className?: string
  // Extra content under the facts (a list, a tick box …).
  children?: React.ReactNode
  // Raw action content (e.g. a Retry button): on its own it sits inline like
  // one action; with `actions` it joins the bottom row, right-aligned.
  footer?: React.ReactNode
  // (Audit fix batch 2) Form fields belonging to the note (a tick + a reason):
  // a plain WHITE section beneath the facts, full width, never on a tint.
  form?: React.ReactNode
}> = ({ title, tone = 'warn', variant = 'card', icon, message, actions, items, status, ariaLabel, labelledBy, describedBy, live, alert, className = '', children, footer, form }) => {
  const id = useId()
  const t = TONE[tone]
  const label = labelledBy ? undefined : ariaLabel ?? (typeof title === 'string' ? title : undefined)
  const role = alert ? 'alert' : live ? 'status' : 'group'
  const all = actions ?? []
  const links = all.filter((a) => kindOf(a) === 'link')
  const mains = all.filter((a) => kindOf(a) !== 'link')
  // (D12) A lone <button> passed as children (older callers) counts as the one action.
  const childButton = !items && !form && !all.length && !footer && React.isValidElement(children) && children.type === 'button' ? children : null
  const body = childButton ? null : children
  // ONE action → inline at the right of the title/text row (never for a
  // checklist, nor when a form sits beneath: its action comes after it).
  const inlineNode = items || form ? null
    : all.length === 1 && !footer ? <Action a={all[0]} whyId={`${id}-i0`} touch />
    : !all.length && footer ? footer
    : childButton
  const doneCount = (items ?? []).filter((i) => i.done).length
  const showProgress = !!items && items.length > 1 && doneCount > 0
  const shell = `relative rounded-[12px] border border-casevo-border bg-casevo-surface overflow-hidden shadow-[0_1px_2px_rgba(16,42,29,0.04)] dark:shadow-none ${className}`
  const strip = <span aria-hidden="true" className={`absolute left-0 inset-y-0 w-[4px] ${t.strip}`} />
  const chip = (size: number) => (
    <span aria-hidden="true" className={`shrink-0 grid place-items-center ${size === 24 ? 'w-[24px] h-[24px] rounded-[7px]' : 'w-[28px] h-[28px] rounded-[8px]'} ${t.chip}`}>
      <BIcon name={icon ?? t.icon} size={size === 24 ? 13 : 15} />
    </span>
  )

  if (variant === 'slim') {
    return (
      <div role={role} aria-label={label} className={shell} data-action-box="slim" data-tone={tone}>
        {strip}
        <div className="flex items-center gap-[12px] pl-[18px] pr-4 py-[10px] flex-wrap">
          {chip(24)}
          {/* (D12) A real basis, so on a phone the link wraps under the line instead of squeezing it. */}
          <p className="m-0 flex-[1_1_200px] min-w-0 text-[13px] text-casevo-text-soft leading-[1.45]">
            {title && <b className="font-semibold text-[13.5px] text-casevo-forest">{title}</b>}
            {status && <span className="ml-[6px]">{status}</span>}
            {message && <span className={title ? 'ml-[6px]' : 'text-casevo-forest'}>{message}</span>}
          </p>
          {actions && actions.length > 0 && <ActionButtons actions={actions} />}
          {footer}
        </div>
        {children}
      </div>
    )
  }

  return (
    <div role={role} aria-label={label} aria-labelledby={labelledBy} aria-describedby={describedBy} className={shell} data-action-box={items ? 'checklist' : 'single'} data-tone={tone}>
      {strip}
      <div className="flex gap-[12px] pl-[18px] pr-4 pt-[13px] pb-[12px]">
        {chip(28)}
        <div className="flex-1 min-w-0 pt-[1px]">
          <div className={inlineNode ? 'flex flex-wrap items-center gap-x-4 gap-y-[10px]' : undefined}>
            <div className={inlineNode ? 'flex-[1_1_240px] min-w-0' : undefined}>
              {(title || showProgress) && (
                <div className="flex items-baseline gap-3">
                  {title && <p className="m-0 flex-1 text-[13.5px] font-semibold text-casevo-forest leading-[1.35]">{title}</p>}
                  {showProgress && <span className="text-[12px] text-casevo-text-soft whitespace-nowrap ml-auto">{doneCount} of {items!.length} done</span>}
                </div>
              )}
              {message && <div className={`text-[13px] leading-[1.45] ${title ? 'mt-[2px] text-casevo-text-soft' : 'text-casevo-forest'}`}>{message}</div>}
            </div>
            {inlineNode && <div className="shrink-0 flex items-center gap-[8px] flex-wrap" data-action-inline="">{inlineNode}</div>}
          </div>
          {items && items.length > 0 && (
            <ul className={`m-0 pl-0 list-none ${title || message ? 'mt-[6px]' : ''}`}>
              {items.map((i, n) => (
                <li key={i.key} data-item={i.key} data-done={i.done ? '' : undefined}
                  className={`flex items-center flex-wrap gap-x-[10px] gap-y-[6px] py-[8px] ${n > 0 ? 'border-t border-casevo-border-soft' : ''}`}>
                  <span aria-hidden="true" className={`shrink-0 w-[18px] h-[18px] rounded-full grid place-items-center ${i.done ? 'bg-casevo-brand text-casevo-surface' : 'border-[1.5px] border-casevo-text-soft'}`}>
                    {i.done && <BIcon name="check" size={11} strokeWidth={3} />}
                  </span>
                  <div className="flex-1 min-w-[min(180px,60%)]">
                    <div className={`text-[13px] leading-[1.4] ${i.done ? 'text-casevo-text-soft' : 'text-casevo-forest'}`}>
                      {i.done && <span className="sr-only">Done: </span>}{i.message}
                    </div>
                    {i.hint && <div className="text-[12px] text-casevo-text-soft leading-[1.4] mt-[1px]">{i.hint}</div>}
                  </div>
                  {i.actions && i.actions.length > 0 && <ActionButtons actions={i.actions} />}
                </li>
              ))}
            </ul>
          )}
          {body}
        </div>
      </div>
      {form && <div className="border-t border-casevo-border-soft bg-casevo-surface px-4 py-[12px] sm:pl-[58px]" data-action-form="">{form}</div>}
      {/* (D12) Several actions / a form's action: a bottom row under a PLAIN
          divider on the card's own white — never a tinted band. */}
      {!inlineNode && (links.length > 0 || mains.length > 0 || footer) && (
        <div className="flex items-center gap-x-[14px] gap-y-[6px] flex-wrap py-[8px] pr-[12px] pl-4 sm:pl-[58px] border-t border-casevo-border-soft bg-casevo-surface" data-action-bar="">
          {links.map((a, n) => <Action key={a.label} a={a} whyId={`${id}-l${n}`} touch />)}
          <span className="flex-1" />
          {mains.map((a, n) => <Action key={a.label} a={a} whyId={`${id}-m${n}`} touch />)}
          {footer}
        </div>
      )}
    </div>
  )
}

export default ActionBox

// (Audit fix batch 2) A refusal / failed load as the red single notice (it
// interrupts: role=alert). `action` (e.g. Dismiss) sits at the right of the text row.
export const ErrorNote: React.FC<{ children: React.ReactNode; title?: React.ReactNode; className?: string; action?: React.ReactNode }> = ({ children, title, className, action }) => (
  <ActionBox alert tone="danger" title={title} message={children} className={className} footer={action} />
)
